const CHUNK_RELOAD_KEY = '__biea_chunk_reload_at__';
const CHUNK_RELOAD_COOLDOWN_MS = 15_000;
const CHUNK_ERROR_PATTERN_LIST = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'ChunkLoadError',
  'Loading chunk',
];

declare global {
  interface Window {
    __bieaChunkLoadRecoveryInstalled__?: boolean;
  }
}

function readChunkReloadAt(): number {
  const storedValue = sessionStorage.getItem(CHUNK_RELOAD_KEY);
  return storedValue ? Number(storedValue) : 0;
}

function canReloadForChunkError(): boolean {
  const lastReloadAt = readChunkReloadAt();
  return !lastReloadAt || Number.isNaN(lastReloadAt)
    || Date.now() - lastReloadAt > CHUNK_RELOAD_COOLDOWN_MS;
}

function markChunkReloadAttempt(): void {
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === 'string' ? message : '';
  }

  return '';
}

function shouldRecoverFromChunkError(error: unknown): boolean {
  const errorMessage = extractErrorMessage(error);
  return CHUNK_ERROR_PATTERN_LIST.some((pattern) => errorMessage.includes(pattern));
}

function reloadForChunkError(error: unknown): boolean {
  if (!shouldRecoverFromChunkError(error) || !canReloadForChunkError()) {
    return false;
  }

  markChunkReloadAttempt();
  console.warn('检测到前端静态资源已更新，正在刷新页面以恢复访问。', error);
  window.location.reload();
  return true;
}

function handleVitePreloadError(event: Event): void {
  const preloadErrorEvent = event as CustomEvent<unknown> & { payload?: unknown };
  if (reloadForChunkError(preloadErrorEvent.payload ?? preloadErrorEvent.detail)) {
    event.preventDefault();
  }
}

function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  if (reloadForChunkError(event.reason)) {
    event.preventDefault();
  }
}

export function installChunkLoadRecovery(): void {
  if (typeof window === 'undefined' || window.__bieaChunkLoadRecoveryInstalled__) {
    return;
  }

  window.__bieaChunkLoadRecoveryInstalled__ = true;
  window.addEventListener('vite:preloadError', handleVitePreloadError as EventListener);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
}
